/**
 * SiteDataCapturePage.tsx — Pre-award & post-award site visit data capture
 *
 * Single printable / PDF-exportable form for the site visit that closes the
 * gap between desk-top modelling and contract-grade specification.
 *
 * Sections: bathymetric survey, sediment cores, water chemistry, hydrology,
 * power audit, footprint walk-down, photographic record, acoustic baseline,
 * pilot-trial provisional location.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ClipboardList, ArrowLeft, Printer, Save, MapPin,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { generateSiteDataCapturePDF } from '@/lib/reservoirPdf';
import { toast } from 'sonner';

const KEY = 'rb_site_data_capture_v1';

interface FieldGroup {
  id: string;
  title: string;
  fields: { key: string; label: string; type?: 'text' | 'textarea' | 'number' | 'checkbox' | 'date'; placeholder?: string }[];
}

const GROUPS: FieldGroup[] = [
  {
    id: 'header', title: '0. Visit details',
    fields: [
      { key: 'visitDate', label: 'Visit date', type: 'date' },
      { key: 'attendees', label: 'Attendees', placeholder: 'Names, organisations' },
      { key: 'weather', label: 'Weather / wind', placeholder: '°C, wind speed & direction, sky' },
      { key: 'permits', label: 'Permits in place', placeholder: 'PTW number, access permits' },
    ],
  },
  {
    id: 'bathy', title: '1. Bathymetric survey',
    fields: [
      { key: 'bathySurveyType', label: 'Survey method', placeholder: 'Multi-beam / RTK echo-sounder / drone LiDAR' },
      { key: 'bathyContractor', label: 'Survey contractor' },
      { key: 'bathyPlannedDate', label: 'Planned survey date', type: 'date' },
      { key: 'bathyGrid', label: 'Grid spacing', placeholder: 'e.g. 1 m × 1 m' },
      { key: 'bathyLowSpot', label: 'Observed low spot location', type: 'textarea', placeholder: 'Approx. GPS or descriptive location' },
      { key: 'bathyInletDeposition', label: 'Inlet deposition lobe observed?', type: 'textarea' },
      { key: 'bathyShoals', label: 'Uncharted shoals / submerged features', type: 'textarea' },
    ],
  },
  {
    id: 'sediment', title: '2. Sediment cores',
    fields: [
      { key: 'sedSampleCount', label: 'Planned core count', type: 'number' },
      { key: 'sedSampleLocs', label: 'Core locations (lat/long or grid)', type: 'textarea' },
      { key: 'sedDepthRange', label: 'Sample depth range (mm)', placeholder: 'e.g. 0–150 mm' },
      { key: 'sedAppearance', label: 'In-situ appearance', type: 'textarea', placeholder: 'Colour, smell, texture, gas evolution' },
      { key: 'sedLabSchedule', label: 'Lab schedule', type: 'textarea', placeholder: 'd50, bulk density, LOI, TP, Fe(II)/Fe(III), SOD respirometry' },
      { key: 'sedLab', label: 'Analytical laboratory' },
      { key: 'sedTurnaround', label: 'Lab turnaround (days)', type: 'number' },
    ],
  },
  {
    id: 'water', title: '3. Water chemistry & CTD',
    fields: [
      { key: 'ctdStations', label: 'CTD station count', type: 'number' },
      { key: 'ctdStationLocs', label: 'CTD station locations', type: 'textarea' },
      { key: 'ctdInterval', label: 'Vertical sampling interval', placeholder: 'e.g. 0.5 m' },
      { key: 'waterDOObserved', label: 'DO observed (mg/L)', placeholder: 'Surface / mid / bed' },
      { key: 'waterTempObserved', label: 'Temperature profile', placeholder: '°C surface / mid / bed' },
      { key: 'waterPHObserved', label: 'pH observed' },
      { key: 'waterTurbidity', label: 'Turbidity observed (NTU)' },
      { key: 'waterNOM', label: 'NOM / TOC sampling planned', type: 'textarea' },
      { key: 'waterSurfactant', label: 'Surfactant index sampling planned', type: 'textarea' },
      { key: 'waterAlgae', label: 'Chlorophyll-a / cyanobacteria sampling planned', type: 'textarea' },
    ],
  },
  {
    id: 'hydro', title: '4. Hydrology & meteorology',
    fields: [
      { key: 'inflowSource', label: 'Inflow source / pumping regime', type: 'textarea' },
      { key: 'inflowRate', label: 'Inflow rate (m³/d)' },
      { key: 'outflowAbstraction', label: 'Abstraction profile' },
      { key: 'metStation', label: 'Nearest MIDAS / met station' },
      { key: 'metRecordYears', label: 'Years of met data available', type: 'number' },
      { key: 'windObserved', label: 'Wind direction observed on visit' },
    ],
  },
  {
    id: 'power', title: '5. Power audit',
    fields: [
      { key: 'powerSupply', label: 'Supply available at embankment', placeholder: 'kVA, 3-phase, voltage' },
      { key: 'powerSpareCapacity', label: 'Spare capacity (kVA)' },
      { key: 'powerHarmonics', label: 'Existing harmonic distortion', placeholder: 'THDi / THDv if known' },
      { key: 'powerRedundancy', label: 'Redundancy / standby generator?' },
      { key: 'powerDistance', label: 'Distance to proposed blower compound (m)' },
      { key: 'powerUpgradeRisk', label: 'Mains upgrade risk', type: 'textarea' },
    ],
  },
  {
    id: 'footprint', title: '6. Blower compound & footprint',
    fields: [
      { key: 'footprintLoc', label: 'Proposed blower compound location' },
      { key: 'footprintGeotech', label: 'Geotechnical observations', type: 'textarea' },
      { key: 'footprintAccess', label: 'Vehicle / crane access', type: 'textarea' },
      { key: 'footprintDuctRoute', label: 'Air-main routing observations', type: 'textarea' },
      { key: 'footprintFlood', label: 'Flood / surface-water risk' },
    ],
  },
  {
    id: 'acoustic', title: '7. Acoustic baseline',
    fields: [
      { key: 'acReceptor1', label: 'Nearest noise-sensitive receptor 1' },
      { key: 'acReceptor1Dist', label: 'Distance (m)', type: 'number' },
      { key: 'acReceptor2', label: 'Nearest noise-sensitive receptor 2' },
      { key: 'acReceptor2Dist', label: 'Distance (m)', type: 'number' },
      { key: 'acBaselineDate', label: 'Baseline survey planned date', type: 'date' },
      { key: 'acStandard', label: 'Standard applied', placeholder: 'BS 4142:2014' },
    ],
  },
  {
    id: 'biosec', title: '8. Biosecurity & ecology',
    fields: [
      { key: 'biosecCheckClean', label: 'Check-Clean-Dry compliance', type: 'checkbox' },
      { key: 'biosecINNS', label: 'INNS species risk noted', type: 'textarea' },
      { key: 'ecoBirds', label: 'Bird / nesting observations', type: 'textarea' },
      { key: 'ecoFish', label: 'Fish / aquatic species observations', type: 'textarea' },
      { key: 'ecoBat', label: 'Bat surveys required', type: 'checkbox' },
    ],
  },
  {
    id: 'pilot', title: '9. Pilot trial provisional siting',
    fields: [
      { key: 'pilotLoc', label: 'Proposed pilot location' },
      { key: 'pilotDepth', label: 'Depth at pilot location (m)', type: 'number' },
      { key: 'pilotIsolation', label: 'Isolation from intake (m)', type: 'number' },
      { key: 'pilotMooring', label: 'Mooring strategy', type: 'textarea' },
      { key: 'pilotInstrumentation', label: 'In-situ instrumentation list', type: 'textarea' },
    ],
  },
  {
    id: 'photo', title: '10. Photographic record',
    fields: [
      { key: 'photoCount', label: 'Number of photos taken', type: 'number' },
      { key: 'photoIndex', label: 'Photo index (filename → location → subject)', type: 'textarea' },
      { key: 'photoDroneFlown', label: 'Drone survey conducted', type: 'checkbox' },
    ],
  },
  {
    id: 'close', title: '11. Visit close-out',
    fields: [
      { key: 'actions', label: 'Outstanding actions', type: 'textarea' },
      { key: 'risks', label: 'New risks identified', type: 'textarea' },
      { key: 'nextVisit', label: 'Next site visit planned', type: 'date' },
      { key: 'signedBy', label: 'Form completed by' },
    ],
  },
];

type FieldValue = string | number | boolean;
type FormState = Record<string, FieldValue>;

export default function SiteDataCapturePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => loadPersisted<FormState>(KEY, {}));

  useEffect(() => { savePersisted(KEY, form); }, [form]);

  const set = (k: string, v: FieldValue) => setForm(f => ({ ...f, [k]: v }));

  const exportPDF = () => {
    generateSiteDataCapturePDF({ form, groups: GROUPS });
    toast.success('Site data capture PDF generated');
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={ClipboardList}
        title="Site Visit — Data Capture Form"
        subtitle="Pre-award & post-award field record covering bathymetry, sediment, water chemistry, hydrology, power, acoustics, biosecurity and pilot siting"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => { savePersisted(KEY, form); toast.success('Saved'); }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={exportPDF}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Export PDF
            </button>
          </div>
        }
      />

      <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-rb-brand mt-0.5 shrink-0" />
          <div className="text-xs text-slate-300">
            This form converts a site visit into a contract-grade data capture record. It is fully
            printable, exports to PDF with CONFIDENTIAL watermark, and the inputs feed directly into the
            tool's modelling once entered.
          </div>
        </div>
      </div>

      {GROUPS.map(group => (
        <div key={group.id} className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-sm font-semibold text-slate-100 mb-3">{group.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.fields.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea value={String(form[f.key] ?? '')}
                    onChange={e => set(f.key, e.target.value)}
                    rows={2}
                    placeholder={f.placeholder}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
                ) : f.type === 'checkbox' ? (
                  <div className="flex items-center">
                    <input type="checkbox" checked={Boolean(form[f.key] ?? false)}
                      onChange={e => set(f.key, e.target.checked)}
                      className="accent-rb-brand w-4 h-4" />
                    <span className="ml-2 text-xs text-slate-400">Yes / done</span>
                  </div>
                ) : (
                  <input type={f.type ?? 'text'}
                    value={String(form[f.key] ?? '')}
                    onChange={e => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400">
        Any unfilled field above is a known data gap. The exported PDF makes these gaps explicit so the
        design team can target follow-up. Methodology and lab specifications referenced are held internally.
      </div>
    </div>
  );
}

export { GROUPS as SITE_DATA_GROUPS };
